import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Textarea } from '@televet/kibble-ui/Textarea';
import { Select } from '@televet/kibble-ui/Select';
import { NumberInput } from '@televet/kibble-ui/NumberInput';
import { Checkbox } from '@televet/kibble-ui/Checkbox';
import { Card } from '@televet/kibble-ui/Card';
import { Icon } from '@televet/kibble-ui/Icon';
import { Swatch } from '@televet/kibble-ui/Swatch';
import { Alert } from '@televet/kibble-ui/Alert';
import { Flex, VStack, Box } from '@televet/kibble-ui/chakra';
import {
  promptTemplateIconMap,
  promptTemplateIconTintMap,
  type PromptTemplateIcon,
  type PromptTemplateIconColor,
} from '@astro/shared-ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import { AudienceField } from '@/features/promptTemplates/AudienceField';
import { formValuesToAudience, type AudienceFormValues } from '@/features/promptTemplates/audience';

export const Route = createFileRoute('/_authenticated/prompt-templates/add')({
  component: AddPromptTemplate,
  beforeLoad: () => {
    return {
      pageTitle: 'Add Prompt Template',
    };
  },
});

export interface PromptTemplateFormData extends AudienceFormValues {
  name: string;
  prompt: string;
  agentId: string;
  description?: string;
  icon?: PromptTemplateIcon | '';
  iconColor?: PromptTemplateIconColor | '';
  isPublished?: boolean;
  autoResponse?: string;
  order?: number;
}

// Matches the VarChar(255) column and the zod cap in promptTemplate's create schema.
const DESCRIPTION_MAX_LENGTH = 255;

const toWords = (pascalCase: string): string => pascalCase.replace(/([A-Z])/g, ' $1').trim();

const iconOptions = Object.entries(promptTemplateIconMap).map(([value, iconName]) => ({
  value,
  label: toWords(value),
  leftElement: <Icon name={iconName} size="sm" />,
}));

const iconColorOptions = Object.entries(promptTemplateIconTintMap).map(([value, tint]) => ({
  value,
  label: tint.label,
  leftElement: <Swatch size="sm" bgColor={tint.backgroundColor} />,
}));

function AddPromptTemplate() {
  const navigate = useNavigate();

  const { data: agentsData } = useQuery(trpc.agent.list.queryOptions({ page: 1, limit: 100 }));

  const { mutate } = useMutation(trpc.promptTemplate.createOne.mutationOptions());

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PromptTemplateFormData>({
    defaultValues: {
      isPublished: false,
      order: 0,
      audienceMode: 'everyone',
      audienceSystems: [],
    },
  });

  const onSubmit = ({ audienceMode, audienceSystems, ...data }: PromptTemplateFormData) => {
    mutate(
      {
        ...data,
        audience: formValuesToAudience({ audienceMode, audienceSystems }),
        prompt: data.prompt.trim(),
        description: data.description?.trim() || null,
        icon: data.icon || null,
        iconColor: data.iconColor || null,
        autoResponse: data.autoResponse?.trim() || null,
        order: data.order ?? 0,
      },
      {
        onSuccess: () => {
          navigate({ to: '/prompt-templates' });
        },
      }
    );
  };

  const onCancel = () => {
    navigate({ to: '/prompt-templates' });
  };

  return (
    <PromptTemplateForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
      control={control}
      watch={watch}
      setValue={setValue}
      title="Add Prompt Template"
      agents={agentsData?.agents || []}
    />
  );
}

export function PromptTemplateForm({
  onSubmit,
  onCancel,
  errors,
  register,
  handleSubmit,
  control,
  watch,
  setValue,
  title,
  agents,
  hasInvalidAudience = false,
}: {
  onSubmit: (data: PromptTemplateFormData) => void;
  onCancel: () => void;
  errors: any;
  register: any;
  handleSubmit: any;
  control: any;
  watch: any;
  setValue: any;
  title: string;
  agents: Array<{ id: string; name: string }>;
  hasInvalidAudience?: boolean;
}) {
  return (
    <Flex
      className="PromptTemplatesForm__Container"
      flex="1 1 auto"
      direction="column"
      align="stretch"
      h="100%"
    >
      <Flex flex="1 1 auto" justify="center" align="flex-start" pt={8}>
        <Card flex="1 1 auto" direction="column" borderColor="border.default" maxW="3xl">
          <Box p={10}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={6} align="stretch">
                <Select
                  label="Agent"
                  data-testid="prompt-template-agent-select"
                  required
                  invalid={!!errors.agentId}
                  errorText={errors.agentId?.message}
                  options={agents.map((agent) => ({
                    value: agent.id,
                    label: agent.name,
                  }))}
                  placeholder="Select an agent"
                  value={watch('agentId') ? [watch('agentId')] : []}
                  onValueChange={(details) => setValue('agentId', details.value[0])}
                />

                <TextInput
                  label="Name"
                  data-testid="prompt-template-name-input"
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                  placeholder="Enter template name"
                  autoFocus
                  {...register('name', { required: 'Name is required' })}
                />

                {/* Controlled, because TextInput's charLimit counter reads the value prop. */}
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    maxLength: {
                      value: DESCRIPTION_MAX_LENGTH,
                      message: 'Description too long',
                    },
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Description (Optional)"
                      data-testid="prompt-template-description-input"
                      charLimit={DESCRIPTION_MAX_LENGTH}
                      invalid={!!errors.description}
                      errorText={errors.description?.message}
                      helperText="One plain-language line saying what the user gets. Shown under the question on the OttoPilot landing card, which omits the line entirely when this is empty."
                      placeholder="Dollar value of lapsing clients, ranked by likelihood to return."
                      name={field.name}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                    />
                  )}
                />

                <Select
                  label="Icon (Optional)"
                  data-testid="prompt-template-icon-select"
                  clearable
                  searchable
                  searchPlaceholder="Search icons..."
                  invalid={!!errors.icon}
                  errorText={errors.icon?.message}
                  helperText="Shown in the tinted square on the landing card. Defaults to Report."
                  options={iconOptions}
                  placeholder="Select an icon"
                  value={watch('icon') ? [watch('icon')] : []}
                  onValueChange={(details) => setValue('icon', details.value[0] ?? '')}
                />

                <Select
                  label="Icon Colour (Optional)"
                  data-testid="prompt-template-icon-color-select"
                  clearable
                  invalid={!!errors.iconColor}
                  errorText={errors.iconColor?.message}
                  helperText="Tint behind the icon. Defaults to Neutral."
                  options={iconColorOptions}
                  placeholder="Select a colour"
                  value={watch('iconColor') ? [watch('iconColor')] : []}
                  onValueChange={(details) => setValue('iconColor', details.value[0] ?? '')}
                />

                <Controller
                  name="order"
                  control={control}
                  render={({ field }) => (
                    <NumberInput
                      label="Order"
                      invalid={!!errors.order}
                      errorText={errors.order?.message}
                      helperText="Lower numbers appear first. Templates with the same order are sorted by last updated."
                      value={String(field.value ?? 0)}
                      onValueChange={(details) =>
                        field.onChange(details.value === '' ? 0 : parseInt(details.value, 10))
                      }
                      min={0}
                      showControls
                    />
                  )}
                />

                <Textarea
                  label="Prompt"
                  invalid={!!errors.prompt}
                  errorText={errors.prompt?.message}
                  placeholder="Enter prompt template"
                  autoresize
                  rows={4}
                  {...register('prompt', { required: 'Prompt is required' })}
                />

                <Textarea
                  label="Auto Response (Optional)"
                  invalid={!!errors.autoResponse}
                  errorText={errors.autoResponse?.message}
                  helperText="If provided, this response will be returned immediately without processing through the agent. Leave empty for normal agent processing."
                  placeholder="Enter auto response (optional)"
                  autoresize
                  rows={4}
                  {...register('autoResponse')}
                />

                {hasInvalidAudience && (
                  <Alert
                    status="warning"
                    hideCloseButton
                    data-testid="prompt-template-audience-invalid-alert"
                    title="The saved audience rule is invalid"
                    description="No clinic sees this template while it is saved this way. Saving replaces it with the audience below."
                  />
                )}
                <AudienceField control={control} />

                <Controller
                  name="isPublished"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value ?? false}
                      onCheckedChange={(details) => field.onChange(details.checked)}
                      helperText="Published templates will be visible in the Otto Pilot feature"
                    >
                      Published
                    </Checkbox>
                  )}
                />

                <Flex justify="flex-end">
                  <Flex gap={2}>
                    <Button variant="secondary" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button type="submit">Save</Button>
                  </Flex>
                </Flex>
              </VStack>
            </form>
          </Box>
        </Card>
      </Flex>
    </Flex>
  );
}
